/**
 * Le clavier réduit un formulaire à quelques lignes sous son en-tête, pied levé au-dessus de lui compris :
 * le champ touché peut alors se retrouver hors de ce qui reste, et on tape à l'aveugle. À l'ouverture du
 * clavier, le défilement va jusqu'au champ qui a le focus. Partagé par `<Modal>` et par
 * `<PageTemplate keyboardAware>` quand son pied se lève.
 */
import type React from "react"
import { useCallback, useEffect, useRef } from "react"
import { Keyboard, TextInput, type View } from "react-native"

import { type ScrollView } from "../atoms/ScrollView"

/** De quoi garder le libellé du champ au-dessus de lui. */
const LABEL_ROOM = 32

export function useFocusedFieldInView(active: boolean, scrollRef?: React.Ref<ScrollView>) {
    const scroll = useRef<ScrollView | null>(null)
    useEffect(() => {
        if (!active) return
        const subscription = Keyboard.addListener("keyboardDidShow", () => {
            const field = TextInput.State.currentlyFocusedInput()
            // La vue du contenu, pas son numéro : voir `GenerateActivityFlow`.
            const content = (
                scroll.current as (ScrollView & { getInnerViewRef: () => View | null }) | null
            )?.getInnerViewRef()
            if (field == null || content == null) return
            field.measureLayout(
                content,
                (_x, y) => scroll.current?.scrollTo({ y: Math.max(0, y - LABEL_ROOM), animated: true }),
                () => undefined
            )
        })
        return () => subscription.remove()
    }, [active])
    // Stable d'un rendu à l'autre : un nouveau ref à chaque rendu détache puis rattache le défilement (et
    // rappelle le `scrollRef` de l'appelant avec `null`) à chaque frappe.
    return useCallback(
        (node: ScrollView | null) => {
            scroll.current = node
            if (typeof scrollRef === "function") scrollRef(node)
            else if (scrollRef != null) (scrollRef as React.MutableRefObject<ScrollView | null>).current = node
        },
        [scrollRef]
    )
}
